import React, { useState, useEffect } from 'react';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonLoading, IonToast } from '@ionic/react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  CircularProgress,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  Autocomplete,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import { Add, Delete, Visibility, CheckCircle, Pending, Warning, Inventory, LocalShipping } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getVendors } from '../../../api/vendor/vendor';
import { getBarInventoryByBranch, getProducts } from '../../../api/products/product';
import { baseURL, config, requestID } from '../../../common/utils';
import { createPurchaseOrder, getPurchaseOrders } from '../../../api/purchase-orders/purchase-orders';
import axios from 'axios';
import { formatToNaira } from '../../../common/utils_t';

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  '&.pending': {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  '&.approved': {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  '&.rejected': {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  '&.fulfilled': {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.dark,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
}));

interface Product {
  id: string;
  product_id: string;
  productName: string;
  name: string;
  sku: string;
  current_stock: number;
  min_stock: number;
  total_quantity_left: number;
}

interface Vendor {
  id: string;
  user_id: string;
  name: string;
  firstName: string;
  lastName: string;
  contact_email: string;
  lead_time: number; // in days
}

interface PurchaseOrderItem {
  id: string;
  product_id: string;
  product_name: string;
  productName?: string;
  quantity: number;
  unit_price: number;
  vendor_id: string;
}

interface PurchaseOrder {
  id: string;
  vendor_id: string;
  vendor_name: string;
  items: PurchaseOrderItem[];
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'fulfilled';
  request_date: string;
  expected_delivery_date?: string;
  notes?: string;
  requested_by: string;
}

const PurchaseOrderRequestPageSales: React.FC = () => {
  // State for products and vendors (would come from your existing lists)
  const [products, setProducts] = useState<Product[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  
  // State for new purchase order
  const [newOrder, setNewOrder] = useState<Omit<PurchaseOrder, 'id'>>({
    vendor_id: '',
    vendor_name: '',
    items: [],
    status: 'draft',
    request_date: new Date().toISOString(),
    requested_by: 'Warehouse Manager' // Would come from auth in real app
  });
  
  const [currentItem, setCurrentItem] = useState<{
    product_id: string;
    product_name: string;
    productName: string;
    quantity: number;
    unit_price: number;
    vendor_id: string;
  }>({
    product_id: '',
    product_name: '',
    productName: '',
    quantity: 1,
    unit_price: 0,
    vendor_id: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentViewOrder, setCurrentViewOrder] = useState<PurchaseOrder | null>(null);

  // Load initial data
  useEffect(() => {
    fetchProducts();
    fetchVendors();
    fetchPurchaseOrders();
  }, []);

  // Mock API calls
  const fetchProducts = async () => {
    setLoading(true);
    try {

    const res = await getBarInventoryByBranch()
    console.log('product',res.data)
      setProducts(res.data);
    } catch (error) {
      showError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await getVendors()
      console.log(res)
      setVendors(res.data.data);
    } catch (error) {
      showError('Failed to load vendors');
    }
  };

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      // Simulate API call
      // const mockOrders: PurchaseOrder[] = [
      //   {
      //     id: 'PO-001',
      //     vendor_id: 'V1',
      //     vendor_name: 'Beverage Distributors Inc',
      //     items: [
      //       { id: '1', product_id: '1', product_name: 'Vodka', quantity: 20, unit_price: 15.99, vendor_id: 'V1' },
      //       { id: '2', product_id: '2', product_name: 'Gin', quantity: 15, unit_price: 18.50, vendor_id: 'V1' }
      //     ],
      //     status: 'approved',
      //     request_date: '2023-05-10T09:30:00',
      //     expected_delivery_date: '2023-05-13T00:00:00',
      //     requested_by: 'Warehouse Manager'
      //   },
      //   {
      //     id: 'PO-002',
      //     vendor_id: 'V2',
      //     vendor_name: 'Premium Spirits Co',
      //     items: [
      //       { id: '3', product_id: '3', product_name: 'Whiskey', quantity: 12, unit_price: 25.75, vendor_id: 'V2' }
      //     ],
      //     status: 'submitted',
      //     request_date: '2023-05-15T11:15:00',
      //     requested_by: 'Warehouse Manager'
      //   }
      // ];

      // const data = {
      //   requestID: requestID(),
      // }

      const response = await getPurchaseOrders()
      console.log(response.data)
      setPurchaseOrders(response.data.data);

    } catch (error) {
      showError('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const showError = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleAddItem = () => {
    if (!currentItem.product_id || !currentItem.quantity || !currentItem.vendor_id) {
      showError('Please fill all item fields');
      return;
    }

    const vendor = vendors.find(v => v.user_id === currentItem.vendor_id);
    if (!vendor) {
      showError('Selected vendor not found');
      return;
    }

    console.log(newOrder)

    setNewOrder(prev => ({
      ...prev,
      vendor_id: currentItem.vendor_id,
      vendor_name: vendor.firstName + ' ' + vendor.lastName,
      items: [
        ...prev.items,
        {
          id: Date.now().toString(),
          product_id: currentItem.product_id,
          product_name: currentItem.product_name || currentItem.productName,
          quantity: currentItem.quantity,
          unit_price: currentItem.unit_price,
          vendor_id: currentItem.vendor_id
        }
      ]
    }));

    // Reset current item
    setCurrentItem({
      product_id: '',
      product_name: '',
      productName: '',
      quantity: 1,
      unit_price: 0,
      vendor_id: ''
    });
  };

  const removeItem = (itemId: string) => {
    setNewOrder(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const submitPurchaseOrder = async () => {
    if (newOrder.items.length === 0) {
      showError('Please add at least one item to the order');
      return;
    }

    setLoading(true);
    try {

      console.log({newOrder})
      // Simulate API call
      const payload = {
        items: newOrder.items,
        amount: newOrder.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0).toFixed(2),
        requestID:requestID()
    
      }

      console.log({payload})
      // const res = await  createPurchaseOrder(payload);
      const res = await  axios.post(`${baseURL}warehouses/createPurchaseOrderSales`, payload, config);
      
      console.log({res})
      
      // Reset the form
      setNewOrder({
        vendor_id: '',
        vendor_name: '',
        items: [],
        status: 'draft',
        request_date: new Date().toISOString(),
        requested_by: 'Warehouse Manager'
      });
fetchPurchaseOrders()
      setToastMessage('Purchase order submitted successfully!');

      setShowToast(true);
      setDialogOpen(false);
    } catch (error) {
      console.log(error)
      showError('Failed to submit purchase order');
    } finally {
      setLoading(false);
    }
  };

  const calculateSuggestedQuantity = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (!product) return 0;
    return product.min_stock - product.total_quantity_left;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const openViewDialog = (order: PurchaseOrder) => {
    setCurrentViewOrder(order);
    setViewDialogOpen(true);
  };

  return (
    <IonPage className='pushDown'>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Vendor Purchase Orders</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2">
                Purchase Order Requests
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<Add />}
                onClick={() => setDialogOpen(true)}
              >
                New Purchase Order
              </Button>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>PO Number</TableCell>
                    {/* <TableCell>Vendor</TableCell> */}
                    <TableCell>Items</TableCell>
                    <TableCell>Request Date</TableCell>
                    <TableCell>Expected Delivery</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {loading ? <CircularProgress size={24} /> : 'No purchase orders found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchaseOrders?.map((order,i) => (
                      <TableRow key={i}>
                        <TableCell>{order?.request_id.toUpperCase()}</TableCell>
                        {/* <TableCell>{order.vendor_name}</TableCell> */}
                        <TableCell>
                          <Chip 
                            label={`${order.items.length} items`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{formatDate(order.request_date)}</TableCell>
                        <TableCell>
                          {order.expected_delivery_date ? formatDate(order.expected_delivery_date) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <StatusChip 
                            label={order.status} 
                            className={order.status}
                            icon={
                              order.status === 'approved' ? <CheckCircle fontSize="small" /> :
                              order.status === 'rejected' ? <Warning fontSize="small" /> :
                              <Pending fontSize="small" />
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View details">
                            <IconButton
                              size="small"
                              onClick={() => openViewDialog(order)}
                              color="primary"
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>

        {/* New Purchase Order Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Create New Purchase Order</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Requested by: {newOrder.requested_by}
              </Typography>
              <Typography variant="subtitle1">
                Request Date: {formatDate(newOrder.request_date)}
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom>
              Add Items
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
 
<FormControl fullWidth>
  <Autocomplete
    options={products || []}
    getOptionLabel={(product) =>
      `${product.name} (Current Qty: ${product.qty})`
    }
    value={
      products.find((p) => p.product_id === currentItem.product_id) || null
    }
    onChange={(event, product) => {
      if (product) {
        setCurrentItem({
          ...currentItem,
          product_id: product.product_id,
          product_name: product.productName || product.name || "",
          quantity: calculateSuggestedQuantity(product.product_id),
        });
      } else {
        // clear if nothing selected
        setCurrentItem({
          ...currentItem,
          product_id: "",
          product_name: "",
          quantity: 1,
        });
      }
    }}
    renderInput={(params) => (
      <TextField {...params} label="Product" variant="outlined" />
    )}
  />
</FormControl>

              </Grid>
              
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={currentItem.quantity}
                  onChange={(e) => setCurrentItem({
                    ...currentItem,
                    quantity: parseInt(e.target.value) || 0
                  })}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
  <Autocomplete
    options={vendors || []}
    getOptionLabel={(vendor) =>
      vendor ? `${vendor.firstName} ${vendor.lastName}` : ""
    }
    value={
      vendors.find((v) => v.user_id === currentItem.vendor_id) || null
    }
    onChange={(event, vendor) => {
      setCurrentItem({
        ...currentItem,
        vendor_id: vendor ? vendor.user_id : "",
      });
    }}
    renderInput={(params) => (
      <TextField {...params} label="Vendor" variant="outlined" />
    )}
  />
</FormControl>
              </Grid>
              
           {/*    <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Unit Price"
                  type="number"
                  value={currentItem.unit_price}
                  onChange={(e) => setCurrentItem({
                    ...currentItem,
                    unit_price: parseFloat(e.target.value) || 0
                  })}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>  */}
              
              <Grid item xs={12} sm={1}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAddItem}
                  sx={{ height: '56px' }}
                >
                  <Add />
                </Button>
              </Grid>
            </Grid>

            {newOrder.items.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Order Items
                </Typography>
                
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                     {/*    <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell> */}
                        <TableCell>Vendor</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {newOrder?.items?.map((item,i) => (
                        <TableRow key={i}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                        {/*   <TableCell align="right">{formatToNaira(item.unit_price.toFixed(2))}</TableCell>
                          <TableCell align="right">{formatToNaira((item.quantity * item.unit_price).toFixed(2))}</TableCell> */}
                          <TableCell>
                            {vendors.find(v => v.user_id === item.vendor_id)?.firstName || 'Unknown'}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton 
                              size="small" 
                              onClick={() => removeItem(item.id)}
                              color="error"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="subtitle1">
                    <strong>Total Items:</strong> {newOrder.items.length}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Total Quantity:</strong> {newOrder.items.reduce((sum, item) => sum + item.quantity, 0)}
                  </Typography>
           {/*        <Typography variant="h6">
                    <strong>Order Total:</strong> 
                    {formatToNaira(newOrder.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0).toFixed(2))}
                  </Typography> */}
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={submitPurchaseOrder}
              variant="contained"
              color="primary"
              disabled={newOrder.items.length === 0 || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <LocalShipping />}
            >
              Submit Purchase Order
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Purchase Order Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          {currentViewOrder && (
            <>
              <DialogTitle>Purchase Order {currentViewOrder.id}</DialogTitle>
              <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1">
                    <strong>Vendor:</strong> {currentViewOrder.vendor_name}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Status:</strong> 
                    <StatusChip 
                      label={currentViewOrder.status} 
                      className={currentViewOrder.status}
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Requested by:</strong> {currentViewOrder.requested_by}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Request Date:</strong> {formatDate(currentViewOrder.request_date)}
                  </Typography>
                  {currentViewOrder.expected_delivery_date && (
                    <Typography variant="subtitle1">
                      <strong>Expected Delivery:</strong> {formatDate(currentViewOrder.expected_delivery_date)}
                    </Typography>
                  )}
                  {currentViewOrder.notes && (
                    <Typography variant="subtitle1" sx={{ mt: 1 }}>
                      <strong>Notes:</strong> {currentViewOrder.notes}
                    </Typography>
                  )}
                </Box>

                <Typography variant="h6" gutterBottom>
                  Order Items
                </Typography>
                
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                      
                        <TableCell>Vendor</TableCell>
                      </TableRow>
                    </TableHead>
                   <TableBody>
  {currentViewOrder?.items?.map(item => (
    <TableRow key={item.id}>
      <TableCell>{item.product_name}</TableCell>
      <TableCell align="right">{item.quantity}</TableCell>
   
      
      <TableCell>
        {vendors.find(v => v.id === item.vendor_id)?.name || 'New Vendor'}
      </TableCell>
    </TableRow>
  ))}
</TableBody>

                  </Table>
                </TableContainer>
                
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="subtitle1">
                    <strong>Total Items:</strong> {currentViewOrder.items.length}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Total Quantity:</strong> {currentViewOrder.items.reduce((sum, item) => sum + item.quantity, 0)}
                  </Typography>
                  {/* <Typography variant="h6">
                    <strong>Order Total:</strong> 
                    {formatToNaira(currentViewOrder.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0).toFixed(2))}
                  </Typography> */}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                {currentViewOrder.status === 'submitted' && (
                  <Button 
                    variant="contained" 
                    color="primary"
                    disabled={loading}
                  >
                    Approve Order
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>

        <IonLoading isOpen={loading} message={'Processing...'} />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
        />
      </IonContent>
    </IonPage>
  );
};

export default PurchaseOrderRequestPageSales;